'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';

interface HostInfoProps {
  name: string;
  avatar: string;
  avatarAlt: string;
  joinedYear: number;
  reviewCount: number;
  rating: number;
  isSuperhost: boolean;
  responseRate: number;
  responseTime: string;
  description: string;
  languages: string[];
}

export default function HostInfo({
  name,
  avatar,
  avatarAlt,
  joinedYear,
  reviewCount,
  rating,
  isSuperhost,
  responseRate,
  responseTime,
  description,
  languages,
}: HostInfoProps) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Meet your host</h2>

      <div className="flex flex-col sm:flex-row gap-8">
        {/* Host card */}
        <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm flex flex-col items-center text-center sm:w-56 flex-shrink-0">
          <div className="relative w-20 h-20 rounded-full overflow-hidden mb-3">
            <AppImage src={avatar} alt={avatarAlt} fill className="object-cover" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">{name}</h3>
          {isSuperhost && (
            <div className="flex items-center gap-1.5 mt-1.5 text-xs font-semibold text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
              <svg className="w-3.5 h-3.5 text-[#4088C6] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Superhost
            </div>
          )}
          <div className="mt-4 grid grid-cols-2 gap-3 w-full text-center">
            <div>
              <div className="text-lg font-bold text-gray-900">{reviewCount}</div>
              <div className="text-xs text-gray-500">Reviews</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-900">{rating.toFixed(1)}</div>
              <div className="text-xs text-gray-500">Rating</div>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">Hosting since {joinedYear}</div>
        </div>

        {/* Host details */}
        <div className="flex-1">
          <p className="text-gray-700 leading-relaxed mb-5">{description}</p>

          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span>Response rate: <strong>{responseRate}%</strong></span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Responds within <strong>{responseTime}</strong></span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              <span>Languages: <strong>{languages.join(', ')}</strong></span>
            </div>
          </div>

          <button className="mt-6 px-6 py-3 border border-gray-900 rounded-xl text-sm font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
            Contact Host
          </button>

          {isSuperhost && (
            <div className="mt-5 p-4 bg-gray-50 rounded-2xl">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-[#4088C6] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-0.5">{name} is a Superhost</p>
                  <p className="text-sm text-gray-600">Superhosts are experienced, highly rated hosts who are committed to providing great stays for guests.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
